import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { IMAGES } from "../lib/images";

export default function RegisterPage() {
  const [form, setForm] = useState({ name: "", email: "", company: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await register(form);
      nav("/portal", { replace: true });
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={IMAGES.elliott_twin_towers} alt="Elliott Towers" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-background/70 via-background/30 to-background/60" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" className="font-serif text-2xl tracking-tight">ELLIOTT</Link>
          <div>
            <div className="overline mb-4">— Request Access</div>
            <h2 className="font-serif text-5xl leading-[1.05] font-light">Join the<br />investor circle.</h2>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16 bg-background">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="w-full max-w-md">
          <div className="overline mb-3">— Create account</div>
          <h1 className="font-serif text-4xl md:text-5xl font-light tracking-tight">Request Portal Access</h1>
          <p className="mt-3 text-sm text-muted-foreground">Accounts are reviewed for institutional verification.</p>

          <form onSubmit={submit} data-testid="register-form" className="mt-10 space-y-5">
            <div>
              <label className="overline mb-2 block">Full name</label>
              <input data-testid="register-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-card border border-input px-5 py-4 focus:border-primary focus:outline-none transition-colors" />
            </div>
            <div>
              <label className="overline mb-2 block">Email</label>
              <input data-testid="register-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-card border border-input px-5 py-4 focus:border-primary focus:outline-none transition-colors" />
            </div>
            <div>
              <label className="overline mb-2 block">Firm / Family Office</label>
              <input data-testid="register-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="w-full bg-card border border-input px-5 py-4 focus:border-primary focus:outline-none transition-colors" />
            </div>
            <div>
              <label className="overline mb-2 block">Password</label>
              <input data-testid="register-password" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full bg-card border border-input px-5 py-4 focus:border-primary focus:outline-none transition-colors" placeholder="At least 8 characters" />
            </div>
            {err && <div data-testid="register-error" className="text-sm text-destructive">{err}</div>}
            <button data-testid="register-submit" type="submit" disabled={busy} className="group w-full inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground px-8 py-5 text-[13px] uppercase tracking-[0.22em] hover:bg-primary/90 transition-all disabled:opacity-60">
              {busy ? "Creating…" : "Create Account"} <ArrowUpRight className="w-4 h-4" />
            </button>
          </form>

          <div className="mt-8 flex items-center justify-between text-sm">
            <Link to="/login" data-testid="register-to-login" className="text-primary hover:underline">Already have access?</Link>
            <Link to="/" className="text-foreground/60 hover:text-primary">← Home</Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
